import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import Disposition from '../interfaces/disposition';
import { BehaviorSubject } from 'rxjs';
import { GET_DISPOSITIONS } from './graphql/dispositions';
import { PipelineMilestone } from '../interfaces/pipeline-milestone';
import Status from '../interfaces/status';
import DateType from '../interfaces/date-type';
import DispositionProceedType from '../interfaces/disposition_proceed_type';
import DispositionType from '../interfaces/disposition-type';

type Paginator = {
  currentPage: number;
  hasMorePages: boolean;
};
@Injectable({
  providedIn: 'root',
})
export class DispositionsService {
  private _dispositions: Disposition[] = [];
  private dispositions = new BehaviorSubject(this._dispositions);
  public dispositions$ = this.dispositions.asObservable();

  private _pipelineMilestones: PipelineMilestone[] = [];
  private pipelineMilestones = new BehaviorSubject(this._pipelineMilestones);
  public pipelineMilestones$ = this.pipelineMilestones.asObservable();

  private _statuses: Status[] = [];
  private statuses = new BehaviorSubject(this._statuses);
  public statuses$ = this.statuses.asObservable();

  private _loading = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _dateTypes: DateType[] = [];
  private dateTypes = new BehaviorSubject(this._dateTypes);
  public dateTypes$ = this.dateTypes.asObservable();

  private _proceedTypes: DispositionProceedType[] = [];
  private proceedTypes = new BehaviorSubject(this._proceedTypes);
  public proceedTypes$ = this.proceedTypes.asObservable();

  private _dispositionTypes: DispositionType[] = [];
  private dispositionTypes = new BehaviorSubject(this._dispositionTypes);
  public dispositionTypes$ = this.dispositionTypes.asObservable();

  constructor(public apollo: Apollo) {}

  public getDispositions(isClosed: boolean) {
    this.setLoading(true);
    let sub = this.apollo
      .watchQuery({
        query: GET_DISPOSITIONS,
        variables: {
          isClosed,
          orderByDate: isClosed ? 'Closed' : 'Disposition Committee',
        },
      })
      .valueChanges.subscribe(({ data, loading }) => {
        let {
          getAllDispositions,
          getPipelineMilestones,
          statusesBySequence,
          dateTypes,
          dispositionProceedTypes,
          dispositionTypes,
        } = data as {
          getAllDispositions: Disposition[];
          getPipelineMilestones: PipelineMilestone[];
          statusesBySequence: Status[];
          dateTypes: DateType[];
          dispositionProceedTypes: DispositionProceedType[];
          dispositionTypes: DispositionType[];
        };
        this.setStatuses(statusesBySequence);
        this.setDispositions(getAllDispositions);
        this.setPipelineMilestones(getPipelineMilestones);
        this.setDateTypes(dateTypes);
        this.setProceedTypes(dispositionProceedTypes);
        this.setDispositionTypes(dispositionTypes);
        this.setLoading(false);
        sub?.unsubscribe();
      });
  }
  private setDispositionTypes(dispositionTypes: DispositionType[]) {
    this._dispositionTypes = dispositionTypes;
    this.dispositionTypes.next(this._dispositionTypes);
  }
  private setDateTypes(dateTypes: DateType[]) {
    this._dateTypes = dateTypes;
    this.dateTypes.next(this._dateTypes);
  }
  private setProceedTypes(proceedTypes: DispositionProceedType[]) {
    this._proceedTypes = proceedTypes;
    this.proceedTypes.next(this._proceedTypes);
  }
  private setStatuses(statuses: Status[]) {
    this._statuses = statuses;
    this.statuses.next(this._statuses);
  }
  private setPipelineMilestones(pipelineMilestones: PipelineMilestone[]) {
    this._pipelineMilestones = pipelineMilestones;
    this.pipelineMilestones.next(pipelineMilestones);
  }
  private setDispositions(dispositions: Disposition[]) {
    this._dispositions = dispositions;
    this.dispositions.next(this._dispositions);
  }
  private setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
  }
}
